/**
 * 30-day session persistence helper (spec §8.1, §10.2).
 * When "Remember me" is checked, the session is persisted in localStorage with a 30-day expiration window.
 * When unchecked, the session is stored in sessionStorage (cleared when browser session ends).
 */

export const REMEMBER_ME_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

export interface RememberStorageAdapter {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export interface RememberStorage {
  getRememberMe: () => boolean;
  setRememberMe: (remember: boolean) => void;
  storage: RememberStorageAdapter;
}

export function createRememberStorage(prefix: string): RememberStorage {
  const REMEMBER_KEY = `${prefix}:remember_me`;
  const EXPIRY_KEY = `${prefix}:remember_until`;

  function getLocal(): Storage | null {
    try {
      return typeof window !== "undefined" && window.localStorage ? window.localStorage : null;
    } catch {
      return null;
    }
  }

  function getSession(): Storage | null {
    try {
      return typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;
    } catch {
      return null;
    }
  }

  return {
    getRememberMe(): boolean {
      const session = getSession();
      const local = getLocal();
      if (!local && !session) return true;
      const sessionVal = session?.getItem(REMEMBER_KEY);
      if (sessionVal !== null && sessionVal !== undefined) return sessionVal === "true";
      const val = local?.getItem(REMEMBER_KEY);
      if (val !== null && val !== undefined) return val === "true";
      return true; // Default to true (remember for 30 days)
    },

    setRememberMe(remember: boolean): void {
      const local = getLocal();
      const session = getSession();
      if (remember) {
        local?.setItem(REMEMBER_KEY, "true");
        local?.setItem(EXPIRY_KEY, String(Date.now() + REMEMBER_ME_DURATION_MS));
        session?.setItem(REMEMBER_KEY, "true");
      } else {
        local?.removeItem(REMEMBER_KEY);
        local?.removeItem(EXPIRY_KEY);
        session?.setItem(REMEMBER_KEY, "false");
      }
    },

    storage: {
      getItem(key: string): string | null {
        const local = getLocal();
        const session = getSession();
        if (!local && !session) return null;

        const isRemembered = local?.getItem(REMEMBER_KEY);
        if (isRemembered === "true") {
          const expiry = local?.getItem(EXPIRY_KEY);
          if (expiry && Date.now() > Number(expiry)) {
            // Expired after 30 days — clear session
            local?.removeItem(key);
            local?.removeItem(REMEMBER_KEY);
            local?.removeItem(EXPIRY_KEY);
            session?.removeItem(key);
            return null;
          }
          let item = local?.getItem(key) ?? null;
          if (!item && local) {
            // Check legacy Supabase storage key (sb-*-auth-token) to migrate existing sessions seamlessly
            for (let i = 0; i < local.length; i++) {
              const k = local.key(i);
              if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
                const legacy = local.getItem(k);
                if (legacy) {
                  local.setItem(key, legacy);
                  item = legacy;
                  break;
                }
              }
            }
          }
          return item;
        }

        if (isRemembered === "false") {
          return session?.getItem(key) ?? null;
        }

        // Default or unconfigured state: check localStorage first, then sessionStorage
        return local?.getItem(key) ?? session?.getItem(key) ?? null;
      },

      setItem(key: string, value: string): void {
        const local = getLocal();
        const session = getSession();
        const sessionVal = session?.getItem(REMEMBER_KEY);
        const localVal = local?.getItem(REMEMBER_KEY);
        const isRemembered =
          sessionVal !== null && sessionVal !== undefined
            ? sessionVal === "true"
            : localVal !== null && localVal !== undefined
              ? localVal === "true"
              : true;

        if (isRemembered) {
          local?.setItem(key, value);
          session?.setItem(key, value);
          if (local && !local.getItem(EXPIRY_KEY)) {
            local.setItem(EXPIRY_KEY, String(Date.now() + REMEMBER_ME_DURATION_MS));
          }
        } else {
          session?.setItem(key, value);
          local?.removeItem(key);
        }
      },

      removeItem(key: string): void {
        const local = getLocal();
        const session = getSession();
        local?.removeItem(key);
        session?.removeItem(key);
        local?.removeItem(REMEMBER_KEY);
        local?.removeItem(EXPIRY_KEY);
        session?.removeItem(REMEMBER_KEY);
      },
    },
  };
}
